import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';

/**
 * HttpErrorInterceptor
 *
 * 全域 HTTP 錯誤處理攔截器，負責：
 * 1. 攔截所有 HTTP 請求錯誤
 * 2. 記錄錯誤日誌
 * 3. 顯示使用者友善錯誤訊息
 * 4. 標準化錯誤處理流程
 */
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private snackBar = inject(MatSnackBar);


  /**
   * 攔截 HTTP 請求並處理錯誤
   */
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // 記錄錯誤到控制台
        this.logError(error);

        // 顯示使用者友善錯誤訊息
        this.showErrorMessage(error);

        // 將錯誤傳遞給訂閱者
        return throwError(() => error);
      })
    );
  }

  /**
   * 記錄錯誤到控制台
   */
  private logError(error: HttpErrorResponse): void {
    const errorDetails = {
      timestamp: new Date().toISOString(),
      status: error.status,
      statusText: error.statusText,
      url: error.url || 'unknown',
      message: error.message,
      error: error.error
    };

    console.error('HTTP Error:', errorDetails);

    // 這裡可以擴展為將錯誤發送到後端日誌系統
    // 例如使用 LoggingService 將錯誤發送到伺服器
  }

  /**
   * 顯示使用者友善錯誤訊息
   */
  private showErrorMessage(error: HttpErrorResponse): void {
    let message = '發生錯誤，請稍後再試';

    // 根據錯誤狀態碼提供更具體的錯誤訊息
    switch (error.status) {
      case 0:
        message = '無法連接到伺服器，請檢查網路連線';
        break;
      case 400:
        message = '請求無效，請檢查輸入資料';
        break;
      case 401:
        message = '未授權，請重新登入';
        break;
      case 403:
        message = '權限不足，無法存取此資源';
        break;
      case 404:
        message = '找不到請求的資源';
        break;
      case 500:
        message = '伺服器內部錯誤，請稍後再試';
        break;
      default:
        // 如果伺服器回傳了自訂錯誤訊息，則使用該訊息
        if (error.error && typeof error.error === 'object' && error.error.message) {
          message = error.error.message;
        } else if (typeof error.error === 'string' && error.error) {
          message = error.error;
        }
    }

    // 使用 MatSnackBar 顯示錯誤訊息
    this.snackBar.open(message, '關閉', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['error-snackbar']
    });
  }
}
